import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import { useClient, useDeleteClient, useUpdateClient } from '../contexts/clients';
import { cleanName, orNull } from '../utils/normalize';
import ClientFields from './fields';
import ClientFarmsPanel from '../layouts/farms-panel';

import '../styles/form.css';

// Edit: local state seeded from useClient(id) via the React 19
// "adjust state during render" pattern. PATCH sends the editable subset.

function buildForm(client) {
    return {
        name:     client.name     || '',
        document: client.document || '',
        email:    client.email    || '',
        phone:    client.phone    || '',
        remarks:  typeof client.remarks === 'string' ? client.remarks : '',
        status:   client.status ?? 2,
    };
}

export default function EditClientForm({ clientId }) {
    const navigate = useNavigate();
    const { data: client, isLoading, error: loadError } = useClient(clientId);
    const { mutateAsync: updateClient, isPending: updating, error: updateError } = useUpdateClient(clientId);
    const { mutateAsync: deleteClient, isPending: deleting } = useDeleteClient();
    const confirm = useConfirm();

    const [form, setForm]           = useState(null);
    const [seededFor, setSeededFor] = useState(null);

    if (client && seededFor !== client.id) {
        setSeededFor(client.id);
        setForm(buildForm(client));
    }

    const fieldError = (field) => updateError?.fields?.[field];
    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    if (isLoading || (!form && !loadError)) return <div className="client-form__state">Cargando cliente…</div>;
    if (loadError || !client) return <div className="client-form__state">Cliente no encontrado.</div>;

    const handleSubmit = async (e) => {
        e.preventDefault();
        const name = cleanName(form.name);
        if (!name) { toast.error('El nombre es obligatorio'); return; }

        try {
            await updateClient({
                name,
                document: orNull(form.document),
                email:    orNull(form.email),
                phone:    orNull(form.phone),
                remarks:  orNull(form.remarks),
                status:   form.status,
            });
            toast.success('Cliente actualizado');
            navigate(`/admin/clients/${clientId}`);
        } catch (err) {
            if (err?.status === 409) toast.error('Ya existe un cliente con ese documento');
            else if (!err?.fields) toast.error(err?.message || 'No se pudo actualizar el cliente');
        }
    };

    const handleDelete = async () => {
        const ok = await confirm({
            title: 'Eliminar cliente',
            message: `¿Eliminar a "${client.name}"?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await deleteClient(clientId);
            toast.success('Cliente eliminado');
            navigate('/admin/clients');
        } catch (err) {
            toast.error(err?.message || 'No se pudo eliminar el cliente');
        }
    };

    return (
        <form className="client-form" onSubmit={handleSubmit}>
            <header className="client-form__header">
                <Link to={`/admin/clients/${clientId}`} className="client-form__back">Volver al cliente</Link>
                <h1 className="client-form__title">Editar cliente</h1>
                <span className="client-form__subtitle">#{client.id}</span>
            </header>

            <ClientFields form={form} setField={setField} fieldError={fieldError} disabled={updating} showStatus />

            <ClientFarmsPanel client={client} />

            <footer className="client-form__footer">
                <Link to={`/admin/clients/${clientId}`} className="client-form__cancel">Cancelar</Link>

                <Gate domain="clients" action="write">
                    <button
                        type="button"
                        className="client-form__danger"
                        onClick={handleDelete}
                        disabled={updating || deleting}
                    >
                        {deleting ? 'Eliminando…' : 'Eliminar'}
                    </button>
                </Gate>

                <button
                    type="submit"
                    className="client-form__submit"
                    disabled={updating || !form.name.trim()}
                >
                    {updating ? 'Guardando…' : 'Guardar cambios'}
                </button>
            </footer>
        </form>
    );
}
